import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { organisationApi, type ContexteOrganisation, type ServiceContexte } from '../../lib/organisationApi';
import { trouverModule } from '../../../shared/catalogueModules.js';

/**
 * Contexte « Établissement · Service » (maquette L01-01).
 *
 * Tant qu'un compte n'a qu'un seul service, rien ne change : pas de sélecteur, `moduleVisible` laisse tout
 * passer (la barre latérale reste celle d'aujourd'hui). Dès qu'il y en a plusieurs, le service choisi
 * filtre les modules rattachés à un service ; les modules transverses (Clients, Facture…) restent toujours visibles.
 */
interface Valeur {
    contexte: ContexteOrganisation | null;
    serviceActif: ServiceContexte | null;
    choisirService: (id: string) => void;
    /** Faux seulement si le sélecteur est visible et que ce module n'appartient pas au service choisi. */
    moduleVisible: (nom: string) => boolean;
    recharger: () => Promise<void>;
}

const Ctx = createContext<Valeur>({
    contexte: null, serviceActif: null, choisirService: () => {}, moduleVisible: () => true, recharger: async () => {},
});

const CLE = 'organisation.service';

export const useOrganisation = () => useContext(Ctx);

export const OrganisationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const [contexte, setContexte] = useState<ContexteOrganisation | null>(null);
    const [choix, setChoix] = useState<string | null>(() => { try { return localStorage.getItem(CLE); } catch { return null; } });
    const concerne = !!user && (user.type === 'client' || user.type === 'collaborator');

    const recharger = useCallback(async () => {
        if (!concerne) { setContexte(null); return; }
        try { setContexte(await organisationApi.contexte()); } catch { setContexte(null); }
    }, [concerne, user?.id]);

    useEffect(() => { void recharger(); }, [recharger]);

    const serviceActif = useMemo(() => {
        if (!contexte || contexte.services.length === 0) return null;
        return contexte.services.find((s) => s.id === choix) || contexte.services[0];
    }, [contexte, choix]);

    const choisirService = useCallback((id: string) => {
        setChoix(id);
        try { localStorage.setItem(CLE, id); } catch { /* stockage indisponible : le choix vaut pour la session */ }
    }, []);

    const moduleVisible = useCallback((nom: string) => {
        if (!contexte || !contexte.selecteur_visible || !serviceActif) return true;
        const m = trouverModule(nom);
        if (!m || m.transverse) return true;
        return serviceActif.modules.includes(nom);
    }, [contexte, serviceActif]);

    const valeur = useMemo(() => ({ contexte, serviceActif, choisirService, moduleVisible, recharger }), [contexte, serviceActif, choisirService, moduleVisible, recharger]);
    return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
};
